"use client";

import { useState } from "react";
import Leaderboard from "./Leaderboard/Leaderboard";
import { Option } from "./Dropdown";
import { isRoundNumber456 } from "../utils/tournaments";
import LeaderboardActionBar from "./LeaderboardActionBar";
import { PGA_TOURNAMENT_ID_SEASON_3 } from "@/utils/constants";
import { TournamentQuery } from "@/types/tournament-query";

export type Gender = "MALE" | "FEMALE";

interface LeaderboardWrapperProps {
  tournamentId: string;
  tournamentName: string;
  tournamentSeason: number;
  startDate: string;
  nationalitiesOptions?: Array<{ label: string; value: string | null }>;
  isNowPlaying: boolean;
  ctpSelectOptions?: Array<number>;
  longestSelectOptions?: Array<number>;
  isMultiRoundTournament: boolean;
  isCTPRangeTournament: boolean;
  roundIds?: Array<string>;
}

export interface QueryVariablesType {
  playNow?: boolean;
  nationality?: string[];
  searchText?: string;
  gender?: Gender;
  participantGroupId?: string;
  kind?: string;
  holeNumber?: number | null;
  roundId?: string;
}

interface BoardType {
  id: number;
  groupId?: string;
  label: string;
  tQuery: TournamentQuery;
  tQueryKey: string;
  tQueryVariables: QueryVariablesType;
}

const LeaderboardWrapper = ({
  tournamentId,
  tournamentName,
  tournamentSeason,
  startDate,
  nationalitiesOptions,
  isNowPlaying,
  ctpSelectOptions,
  longestSelectOptions,
  isMultiRoundTournament,
  isCTPRangeTournament,
  roundIds,
}: LeaderboardWrapperProps) => {
  const isPGATournamentS3 = tournamentId === PGA_TOURNAMENT_ID_SEASON_3;
  const [tournamentQueryKey, setTournamentQueryKey] = useState<number | string>(
    1
  );
  const [tournamentQuery, setTournamentQuery] = useState<TournamentQuery>(
    isCTPRangeTournament ? "ctpRangeLeaderboard" : "leaderboard"
  );
  const [queryVariables, setQueryVariables] = useState({});
  const [selectedBoard, setSelectedBoard] = useState(1);
  const [selectedCTPBoard, setSelectedCTPBoard] = useState(0);
  const [selectedLongestBoard, setSelectedLongestBoard] = useState(0);

  const handleApplyFilters = (filters: object) => {
    setQueryVariables({ ...queryVariables, ...filters });
  };

  const ctpOptions = ctpSelectOptions
    ? ctpSelectOptions.map((holeNumber, index) => ({
        id: 1 + index,
        label: `CTP: Hole ${holeNumber}`,
        tQuery: "closestToPinLeaderboard" as TournamentQuery,
        tQueryKey: `ctp-${holeNumber}_`,
        tQueryVariables: {
          holeNumber,
        },
      }))
    : [];

  const longestRoundOptions = longestSelectOptions
    ? longestSelectOptions.map((holeNumber, index) => ({
        id: 1 + index,
        label: `Hole ${holeNumber}`,
        tQuery: "longestDrive" as TournamentQuery,
        tQueryKey: `longestDrive-${holeNumber}_`,
        tQueryVariables: {
          holeNumber,
        },
      }))
    : [];

  const selectOptions: BoardType[] = [
    {
      id: 1,
      label: "Stroke play",
      tQuery: "leaderboard" as TournamentQuery,
      tQueryKey: "",
      tQueryVariables: {},
    },
    {
      id: 2,
      label: "Closest to the pin",
      tQuery: "aggregatedClosestToPinLeaderboard" as TournamentQuery,
      tQueryKey: "ctp_",
      tQueryVariables: {},
    },
    {
      id: 3,
      label: "Birdie streak",
      tQuery: "birdieStreak",
      tQueryKey: "birdie_" as TournamentQuery,
      tQueryVariables: {
        kind: "BIRDIE_STREAK",
      },
    },
    {
      id: 4,
      label: "Longest drive - men",
      tQuery: "longestDrive" as TournamentQuery,
      tQueryKey: "l_drive_men_",
      tQueryVariables: isMultiRoundTournament
        ? {
            gender: "MALE",
            holeNumber:
              longestSelectOptions && longestSelectOptions.length > 0
                ? longestSelectOptions?.[0]
                : null,
            roundId: roundIds?.[roundIds?.length - 1],
          }
        : {
            gender: "MALE",
          },
    },
    {
      id: 5,
      label: "Longest drive - women",
      tQuery: "longestDrive" as TournamentQuery,
      tQueryKey: "l_drive_women_",
      tQueryVariables: isMultiRoundTournament
        ? {
            gender: "FEMALE",
            holeNumber:
              longestSelectOptions && longestSelectOptions.length > 0
                ? longestSelectOptions?.[0]
                : null,
            roundId: roundIds?.[roundIds?.length - 1],
          }
        : {
            gender: "FEMALE",
          },
    },
    {
      id: 6,
      label: "Most GIR",
      tQuery: "greensInRegulation" as TournamentQuery,
      tQueryKey: "gir_",
      tQueryVariables: {
        kind: "GREENS_IN_REGULATION",
      },
    },
  ];

  const handleSelectChange = (board: BoardType) => {
    if (board.id !== selectedBoard) {
      setTournamentQuery(board.tQuery);
      setSelectedBoard(board.id);
      setQueryVariables(board.tQueryVariables);
      setTournamentQueryKey(board.tQueryKey);
    }
  };

  const handleCTPSelectChange = (board: BoardType) => {
    if (board.id !== selectedCTPBoard) {
      setTournamentQuery(board.tQuery);
      setSelectedCTPBoard(board.id);
      setQueryVariables(board.tQueryVariables);
      setTournamentQueryKey(board.tQueryKey);
    }
  };

  const handleLongestSelectChange = (board: BoardType) => {
    if (board.id !== selectedLongestBoard) {
      setTournamentQuery(board.tQuery);
      setSelectedLongestBoard(board.id);
      setTournamentQueryKey(board.tQueryKey);

      if (
        isMultiRoundTournament &&
        (selectedBoard === 4 || selectedBoard === 5)
      ) {
        const selectedBoardOpt = selectOptions.find(
          (option) => option.id === selectedBoard
        );
        // pass gender from longest drive selected option to selected hole
        setQueryVariables({
          ...selectedBoardOpt?.tQueryVariables,
          ...board.tQueryVariables,
        });
      } else setQueryVariables(board.tQueryVariables);
    }
  };

  const dropdownOptions = () => {
    const isEpson = tournamentName.toLowerCase().includes("epson");
    if (isEpson) {
      return selectOptions.filter(({ id }) => id !== 3 && id !== 4 && id !== 6);
    }
    if (tournamentSeason === 4 || tournamentSeason === 5) {
      if (isCTPRangeTournament) {
        return [
          {
            id: 1,
            label: "Closest to the pin",
            tQuery: "ctpRangeLeaderboard" as TournamentQuery,
            tQueryKey: "ctp-range_",
            tQueryVariables: {},
          },
        ];
      }
      return selectOptions.filter(({ id }) => id !== 3 && id !== 6);
    }

    if (tournamentSeason === 3) {
      if (isPGATournamentS3) {
        return [
          {
            id: 1,
            label: "Stroke play",
            tQuery: "leaderboard" as TournamentQuery,
            tQueryKey: "",
            tQueryVariables: {},
          },
          {
            id: 2,
            label: "Closest to the pin",
            tQuery: "closestToPinLeaderboard" as TournamentQuery,
            tQueryKey: `ctp-${ctpSelectOptions && ctpSelectOptions[0]}_`,
            tQueryVariables: {
              holeNumber:
                ctpSelectOptions && ctpSelectOptions.length > 0
                  ? ctpSelectOptions[0]
                  : null,
            },
          },
        ];
      }
      return selectOptions.filter(({ id }) => id !== 3 && id !== 6);
    }

    if (tournamentSeason === 2 || tournamentSeason === 1) {
      if (isRoundNumber456(tournamentName as string)) {
        return selectOptions.slice(0, selectOptions.length - 1);
      }

      if (tournamentName?.toLowerCase()?.includes("final")) {
        return selectOptions.slice(0, 2);
      }
    }

    return selectOptions;
  };

  return (
    <section className="flex">
      <div className="grid-container w-full mx-0 md:mx-auto [&>*]:mx-auto [&>*]:my-0">
        <div className="col-start-1 col-span-4 lg:col-start-2 lg:col-span-14 2xl:col-start-4 2xl:col-span-26 w-full">
          <LeaderboardActionBar
            nationalitiesOptions={nationalitiesOptions}
            defaultDropdownOption={dropdownOptions()[0] as Option}
            dropdownOptions={dropdownOptions()}
            isNowPlaying={!!isNowPlaying}
            startDate={startDate}
            tournamentQuery={tournamentQuery}
            onSelectChange={handleSelectChange}
            onApplyFilters={handleApplyFilters}
            tournamentName={tournamentName}
            isCTPLeaderboard={selectedBoard === 2}
            isMultiRoundTournament={isMultiRoundTournament}
            ctpDefaultDropdownOption={
              !isPGATournamentS3
                ? // && !isMultiRoundTournament
                  ({
                    id: 0,
                    label: "Aggregated",
                    tQuery:
                      "aggregatedClosestToPinLeaderboard" as TournamentQuery,
                    tQueryKey: "ctp_",
                    tQueryVariables: {},
                  } as Option)
                : (ctpOptions[0] as Option)
            }
            ctpDropdownOptions={
              !isPGATournamentS3
                ? // && !isMultiRoundTournament
                  [
                    {
                      id: 0,
                      label: "Aggregated",
                      tQuery:
                        "aggregatedClosestToPinLeaderboard" as TournamentQuery,
                      tQueryKey: "ctp_",
                      tQueryVariables: {},
                    } as Option,
                    ...ctpOptions,
                  ]
                : ctpOptions
            }
            longestOptions={longestRoundOptions}
            onCTPSelectChange={handleCTPSelectChange}
            onLongestSelectChange={handleLongestSelectChange}
            defaultRoundOption={
              isMultiRoundTournament
                ? { id: 2, label: "Round 2" }
                : { id: 1, label: "Round 1" }
            }
            roundOptions={
              isMultiRoundTournament
                ? [
                    {
                      ...ctpOptions[0],
                      id: "round2",
                      label: "Round 2",
                      tQueryVariables: {
                        ...ctpOptions[0]?.tQueryVariables,
                        roundId: roundIds?.[roundIds?.length - 1],
                      },
                    },
                  ]
                : [
                    {
                      ...ctpOptions[0],
                      id: "round1",
                      label: "Round 1",
                      tQueryVariables: {
                        ...ctpOptions[0]?.tQueryVariables,
                        roundId: roundIds?.[0],
                      },
                    },
                    {
                      ...ctpOptions[0],
                      id: "round2",
                      label: "Round 2",
                      tQueryVariables: {
                        ...ctpOptions[0]?.tQueryVariables,
                        roundId: roundIds?.[1],
                      },
                    },
                  ]
            }
          />
        </div>
        <div className="col-start-1 col-span-4 lg:col-start-2 lg:col-span-14 2xl:col-start-4 2xl:col-span-26 w-full md:my-0 md:mx-[15px] lg:m-auto">
          <Leaderboard
            isCTPRangeTournament={isCTPRangeTournament}
            isMultiRoundTournament={isMultiRoundTournament}
            tournamentId={tournamentId}
            tournamentQueryKey={tournamentQueryKey}
            tournamentQuery={tournamentQuery}
            queryVariables={queryVariables}
            tournamentName={tournamentName}
          />
        </div>
      </div>
    </section>
  );
};

export default LeaderboardWrapper;
